/**
 * 品牌模板预览组件 — 用 <video> + CSS 叠加字幕样式。
 *
 * 参考 reap.video 的实现：
 * - 预览始终播放 main clip（talking-head）+ 全部 overlays，不切换到 intro/outro 视频
 * - Intro/Outro 是拼接素材，用底部时间线条标注其拼接位置，不在预览中播放
 * - 字幕层由 captionStyleConfigMap 驱动 DOM 渲染：双层 span + 高亮 + 逐词 CSS 动画
 * - 字幕 / Logo / Audiogram / Text 可拖拽位置、可 resize 尺寸、可选中
 * - 播放/暂停/循环控件
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, Repeat } from 'lucide-react'
import { appApiBaseUrl } from '@/shared/api/app-client'
import { cn } from '@/shared/ui/cn'
import {
  buildPreviewCaptionCues,
  type CaptionAnimation,
  type CaptionHighlightMode,
  type CaptionRenderConfig,
  type CaptionIdleAnimation,
} from './caption-style-config'

/**
 * 可变换 overlay 的变更回调。
 * 坐标与尺寸统一使用预览区域的百分比（0-100），便于不同预览尺寸和输出比例复用。
 * - 拖拽移动：只填 x/y
 * - resize：填 width/height，并视角点跟随语义回填 x/y
 */
export type OverlayChange = {
  type: 'caption' | 'logo' | 'audiogram' | 'text'
  id?: string | number
  x?: number
  y?: number
  width?: number
  height?: number
}

/** 向后兼容旧引用 */
export type OverlayPositionChange = OverlayChange

export interface TemplatePreviewConfig {
  aspectRatio?: string
  resolution?: string
  backgroundColor?: string
  background?: { assetId?: number; publicUrl?: string; fileName?: string; fit?: 'cover' | 'contain' | 'stretch' } | null
  backgroundEnabled?: boolean
  logos?: Array<{ assetId?: number; publicUrl?: string; fileName?: string; position?: string; scale?: string; x?: number; y?: number; width?: number; height?: number }>
  logoEnabled?: boolean
  intro?: { assetId?: number; publicUrl?: string; fileName?: string; durationMs?: number } | null
  outro?: { assetId?: number; publicUrl?: string; fileName?: string; durationMs?: number } | null
  captions?: { enabled?: boolean; x?: number; y?: number; width?: number }
  captionStyleKey?: string
  audiogramPreset?: string
  audiogram?: { enabled?: boolean; x?: number; y?: number; width?: number; height?: number }
  textPreset?: string
  text?: {
    enabled?: boolean
    x?: number
    y?: number
    width?: number
    height?: number
    content?: string
    fontFamily?: string
    align?: 'left' | 'center' | 'right'
    color?: string
    accentColor?: string
    fontWeight?: number
    italic?: boolean
    uppercase?: boolean
    fontSize?: number
    lineHeight?: number
    letterSpacing?: number
    rotation?: number
    animation?: string
  }
  logoLayout?: string
  backgroundPreset?: string
}

export type VariantPreviewConfig = CaptionRenderConfig

const PREVIEW_VIDEO = '/templates/lex-fridman.mp4'

function resolveUrl(url?: string): string | undefined {
  if (!url) return undefined
  if (url.startsWith('/uploads/')) return `${appApiBaseUrl}${url}`
  return url
}

/** Logo 位置 → 初始 CSS 定位（百分比） */
function logoInitialPosition(position: string | undefined): { left: number; top: number } {
  switch (position) {
    case 'top-left': return { left: 5, top: 5 }
    case 'top-right': return { left: 85, top: 5 }
    case 'bottom-left': return { left: 5, top: 85 }
    case 'center': return { left: 42, top: 45 }
    case 'bottom-right':
    default: return { left: 85, top: 85 }
  }
}

type Corner = 'nw' | 'ne' | 'sw' | 'se'

const CORNER_CURSORS: Record<Corner, string> = {
  nw: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  se: 'nwse-resize',
}

function logoScaleWidth(scale: string | undefined): string {
  const map: Record<string, string> = { small: '8%', medium: '12%', large: '18%' }
  return map[scale ?? 'medium'] ?? '12%'
}
void logoScaleWidth

/**
 * 字体名 → CSS font-stack 映射。
 * 同时用于 Text overlay 和 Caption 渲染，保证选字体后预览真实变化。
 * 配合 Google Fonts <link> 加载 Web 字体（Bebas Neue / Space Grotesk / Playfair Display / DM Sans）。
 */
function resolveFontStack(fontFamily: string | undefined) {
  switch ((fontFamily ?? '').trim()) {
    case 'Komika Axis':
      return '"Komika Axis", "Arial Black", Impact, sans-serif'
    case 'Bebas Neue':
      return '"Bebas Neue", "Arial Narrow", Impact, sans-serif'
    case 'Space Grotesk':
      return '"Space Grotesk", "Trebuchet MS", "Segoe UI", sans-serif'
    case 'Playfair Display':
      return '"Playfair Display", Georgia, "Times New Roman", serif'
    case 'DM Sans':
      return '"DM Sans", Inter, "Segoe UI", sans-serif'
    case 'JetBrains Mono':
      return '"JetBrains Mono", "SFMono-Regular", Consolas, monospace'
    case 'boldslay':
    case 'Bangers':
      return `"${fontFamily}", Impact, "Arial Black", sans-serif`
    case 'Avenir Next':
    case 'Helvetica Neue':
      return `"${fontFamily}", "Segoe UI", Arial, sans-serif`
    case 'Barriecito':
    case 'Fredoka':
    case 'Bungee':
    case 'Changa':
    case 'Anson':
    case 'Epilogue':
      return `"${fontFamily}", Inter, "Segoe UI", sans-serif`
    case 'Inter':
      return 'Inter, "Segoe UI", sans-serif'
    default:
      return fontFamily ? `"${fontFamily}", sans-serif` : 'Inter, "Segoe UI", sans-serif'
  }
}

function getTextPresetPreview(
  preset: string | undefined,
  textConfig?: TemplatePreviewConfig['text'],
) {
  const customContent = typeof textConfig?.content === 'string' ? textConfig.content.trim() : ''
  const customLines = customContent ? customContent.split(/\r?\n/).filter(Boolean) : null
  const customColor = textConfig?.color
  const customAccent = textConfig?.accentColor
  const customFamily = resolveFontStack(textConfig?.fontFamily)
  const customWeight = textConfig?.fontWeight
  const customItalic = textConfig?.italic
  const customUppercase = textConfig?.uppercase
  const customFontSize = textConfig?.fontSize
  const customLineHeight = textConfig?.lineHeight
  const customLetterSpacing = textConfig?.letterSpacing
  const customRotation = textConfig?.rotation

  switch (preset) {
    case 'impact':
      return { lines: customLines ?? ['制造', '冲击'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 900, fontSize: `${customFontSize ?? 26}px`, lineHeight: customLineHeight ?? 0.92, letterSpacing: `${customLetterSpacing ?? -0.05}em`, textTransform: customUppercase === false ? 'none' : 'uppercase' as const, fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'sliced':
      return { lines: customLines ?? ['切片'], style: { color: customColor ?? '#FF5C7A', fontFamily: customFamily, fontWeight: customWeight ?? 900, fontSize: `${customFontSize ?? 28}px`, lineHeight: customLineHeight ?? 0.96, letterSpacing: `${customLetterSpacing ?? -0.04}em`, textTransform: customUppercase === false ? 'none' : 'uppercase' as const, fontStyle: customItalic ? 'italic' : 'normal', transform: `rotate(${customRotation ?? -12}deg)` } }
    case 'minimal':
      return { lines: customLines ?? ['少', '即', '是多'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 700, fontSize: `${customFontSize ?? 24}px`, lineHeight: customLineHeight ?? 0.95, letterSpacing: `${customLetterSpacing ?? -0.03}em`, textTransform: customUppercase ? 'uppercase' as const : 'none', fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'highlight':
      return { lines: customLines ?? ['重点', '文字'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 900, fontSize: `${customFontSize ?? 24}px`, lineHeight: customLineHeight ?? 0.95, letterSpacing: `${customLetterSpacing ?? -0.04}em`, textTransform: customUppercase === false ? 'none' : 'uppercase' as const, background: `linear-gradient(135deg, ${customAccent ?? '#5B4BFF'} 0%, ${customColor ?? '#4A35F0'} 100%)`, padding: '10px 18px', borderRadius: '10px', fontStyle: customItalic ? 'italic' : 'normal', transform: `rotate(${customRotation ?? 0}deg)` } }
    case 'gradient':
      return { lines: customLines ?? ['渐变样式'], style: { color: customColor ?? '#FF9800', fontFamily: customFamily, fontWeight: customWeight ?? 800, fontSize: `${customFontSize ?? 24}px`, lineHeight: customLineHeight ?? 0.98, letterSpacing: `${customLetterSpacing ?? -0.03}em`, textTransform: customUppercase ? 'uppercase' as const : 'none', fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'neon':
      return { lines: customLines ?? ['霓虹', '未来'], style: { color: customColor ?? '#38DFFF', fontFamily: customFamily, fontWeight: customWeight ?? 900, fontSize: `${customFontSize ?? 26}px`, lineHeight: customLineHeight ?? 0.92, letterSpacing: `${customLetterSpacing ?? -0.04}em`, textTransform: customUppercase === false ? 'none' : 'uppercase' as const, fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'avant':
      return { lines: customLines ?? ['前卫', '风格'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 800, fontSize: `${customFontSize ?? 25}px`, lineHeight: customLineHeight ?? 0.92, letterSpacing: `${customLetterSpacing ?? -0.05}em`, textTransform: customUppercase ? 'uppercase' as const : 'none', fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'glass':
      return { lines: customLines ?? ['玻璃', '效果'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 900, fontSize: `${customFontSize ?? 24}px`, lineHeight: customLineHeight ?? 0.92, letterSpacing: `${customLetterSpacing ?? -0.04}em`, textTransform: customUppercase === false ? 'none' : 'uppercase' as const, fontStyle: customItalic ? 'italic' : 'normal' } }
    case 'modern':
    default:
      return { lines: customLines ?? ['现代标题'], style: { color: customColor ?? '#FFFFFF', fontFamily: customFamily, fontWeight: customWeight ?? 800, fontSize: `${customFontSize ?? 24}px`, lineHeight: customLineHeight ?? 1, letterSpacing: `${customLetterSpacing ?? -0.035}em`, textTransform: customUppercase ? 'uppercase' as const : 'none', fontStyle: customItalic ? 'italic' : 'normal' } }
  }
}

function getTextAnimationName(animation: string | undefined): string | undefined {
  switch (animation) {
    case 'none':
      return undefined
    case 'fade':
      return 'textFadeIn'
    case 'scale':
      return 'textScaleIn'
    case 'bounce':
      return 'textBounceIn'
    case 'flip':
      return 'textFlipIn'
    case 'zoom':
      return 'textZoomIn'
    case 'slide':
      return 'textSlideUp'
    case 'snap':
      return 'textSnapIn'
    case 'glitch':
      return 'textGlitchIn'
    case 'swipe':
      return 'textSwipeIn'
    case 'float':
      return 'textFloatIn'
    case 'spin':
      return 'textSpinIn'
    case 'slide-d':
      return 'textSlideDownIn'
    case 'slide-l':
      return 'textSlideLeftIn'
    case 'diagonal':
      return 'textDiagonalIn'
    case 'wobble':
      return 'textWobbleIn'
    case 'flip-y':
      return 'textFlipYIn'
    case 'pulse':
      return 'textPulseIn'
    case 'drop':
      return 'textDropIn'
    case 'squeeze':
      return 'textSqueezeIn'
    case 'roll':
      return 'textRollIn'
    case 'swing':
      return 'textSwingIn'
    case 'expand':
      return 'textExpandIn'
    case 'expand-h':
      return 'textExpandHIn'
    case 'twist':
      return 'textTwistIn'
    case 'blur':
      return 'textBlurIn'
    case 'spiral':
      return 'textSpiralIn'
    case 'shake':
      return 'textShakeIn'
    case 'curtain':
      return 'textCurtainIn'
    case 'fold':
      return 'textFoldIn'
    case 'zigzag':
      return 'textZigzagIn'
    case 'elastic':
      return 'textElasticIn'
    case 'slingshot':
      return 'textSlingshotIn'
    case 'rotate':
      return 'textRotateIn'
    case 'skew':
      return 'textSkewIn'
    case 'peek':
      return 'textPeekIn'
    case 'vortex':
      return 'textVortexIn'
    default:
      return 'textFadeIn'
  }
}

/**
 * 可变换的覆盖元素：支持拖拽移动 + 四角 resize + selected 态。
 *
 * 用 Pointer Events 实现：
 * - pointerdown 记录起点 + 父容器尺寸，setPointerCapture 保证拖出元素仍收事件
 * - pointermove 期间用临时偏移更新视觉，不污染上层配置
 * - pointerup 把最终百分比位置/尺寸通过 onChange 提交给上层
 *
 * 位置与尺寸都通过百分比受控传入（0-100）。
 * resize 采用“角点跟随”语义：拖动某个角时，对角点固定，x/y/width/height 一起更新。
 */
function TransformableOverlay({
  children,
  overlayId,
  overlayType,
  selectedId,
  onSelect,
  leftPercent,
  topPercent,
  widthPercent,
  heightPercent,
  minWidthPercent = 4,
  minHeightPercent = 4,
  onChange,
  resizable = true,
}: {
  children: React.ReactNode
  overlayId: string
  overlayType: OverlayChange['type']
  selectedId: string | null
  onSelect?: (id: string) => void
  leftPercent: number
  topPercent: number
  widthPercent?: number
  heightPercent?: number
  minWidthPercent?: number
  minHeightPercent?: number
  onChange?: (change: OverlayChange) => void
  resizable?: boolean
}) {
  const overlayRef = useRef<HTMLDivElement>(null)
  // 拖拽/resize 过程中的临时像素偏移与尺寸增量，松手后清零
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [resizeDelta, setResizeDelta] = useState<{ dw: number; dh: number; dx: number; dy: number } | null>(null)
  // 拖动中标记，用于描边反馈
  const [isDragging, setIsDragging] = useState(false)
  // 没有上层回调时，用本地状态承接松手后的位置/尺寸，让元素停在拖后状态
  const [localPos, setLocalPos] = useState<{ x: number; y: number } | null>(null)
  const [localSize, setLocalSize] = useState<{ w: number; h: number } | null>(null)

  const isSelected = selectedId === overlayId

  // 当前生效的位置/尺寸：优先用本地状态，否则用上层传入的受控值
  const currentLeft = localPos?.x ?? leftPercent
  const currentTop = localPos?.y ?? topPercent
  const currentWidth = localSize?.w ?? widthPercent
  const currentHeight = localSize?.h ?? heightPercent

  // 一次交互（move 或 resize）的共享状态
  const interaction = useRef<{
    pointerId: number
    mode: 'move' | 'resize'
    corner?: Corner
    startX: number
    startY: number
    parentRect: DOMRect
  } | null>(null)

  const beginInteraction = useCallback(
    (e: React.PointerEvent, mode: 'move' | 'resize', corner?: Corner) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return
      const node = overlayRef.current
      const parent = node?.parentElement
      if (!parent) return
      e.preventDefault()
      e.stopPropagation()
      onSelect?.(overlayId)
      const parentRect = parent.getBoundingClientRect()
      interaction.current = {
        pointerId: e.pointerId,
        mode,
        corner,
        startX: e.clientX,
        startY: e.clientY,
        parentRect,
      }
      node?.setPointerCapture(e.pointerId)
      if (mode === 'move') {
        setDragOffset({ x: 0, y: 0 })
        setIsDragging(true)
      } else {
        setResizeDelta({ dw: 0, dh: 0, dx: 0, dy: 0 })
      }
    },
    [onSelect, overlayId],
  )

  const onPointerDownMove = useCallback((e: React.PointerEvent) => beginInteraction(e, 'move'), [beginInteraction])
  const makeResizeDown = useCallback(
    (corner: Corner) => (e: React.PointerEvent) => beginInteraction(e, 'resize', corner),
    [beginInteraction],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const state = interaction.current
      if (!state || e.pointerId !== state.pointerId) return
      const dx = e.clientX - state.startX
      const dy = e.clientY - state.startY
      if (state.mode === 'move') {
        setDragOffset({ x: dx, y: dy })
      } else {
        // 角点跟随：拖动角移动多少，对角点固定
        // nw: 左上角动 → dx/dy 体现为左边/上边移动（dx 为右移时宽度变小）
        const c = state.corner!
        setResizeDelta({
          dw: c === 'nw' || c === 'sw' ? -dx : dx,
          dh: c === 'nw' || c === 'ne' ? -dy : dy,
          dx: c === 'nw' || c === 'sw' ? dx : 0,
          dy: c === 'nw' || c === 'ne' ? dy : 0,
        })
      }
    }
    const onUp = (e: PointerEvent) => {
      const state = interaction.current
      if (!state || e.pointerId !== state.pointerId) return
      const node = overlayRef.current
      node?.releasePointerCapture(state.pointerId)
      const parentRect = state.parentRect
      const dx = e.clientX - state.startX
      const dy = e.clientY - state.startY
      interaction.current = null

      if (parentRect.width <= 0 || parentRect.height <= 0) {
        setDragOffset({ x: 0, y: 0 })
        setResizeDelta(null)
        setIsDragging(false)
        return
      }

      if (state.mode === 'move') {
        setDragOffset({ x: 0, y: 0 })
        setIsDragging(false)
        const nextX = Math.max(0, Math.min(100, currentLeft + (dx / parentRect.width) * 100))
        const nextY = Math.max(0, Math.min(100, currentTop + (dy / parentRect.height) * 100))
        const change: OverlayChange = { type: overlayType, id: overlayId, x: nextX, y: nextY }
        if (onChange) {
          onChange(change)
        } else {
          setLocalPos({ x: nextX, y: nextY })
        }
      } else {
        setResizeDelta(null)
        const c = state.corner!
        const dwdx = c === 'nw' || c === 'sw' ? -dx : dx
        const dhdy = c === 'nw' || c === 'ne' ? -dy : dy
        const shiftX = c === 'nw' || c === 'sw' ? dx : 0
        const shiftY = c === 'nw' || c === 'ne' ? dy : 0
        const dwPct = (dwdx / parentRect.width) * 100
        const dhPct = (dhdy / parentRect.height) * 100
        const dxPct = (shiftX / parentRect.width) * 100
        const dyPct = (shiftY / parentRect.height) * 100

        const baseW = currentWidth ?? 0
        const baseH = currentHeight ?? 0
        const nextW = Math.max(minWidthPercent, baseW + dwPct)
        const nextH = heightPercent != null ? Math.max(minHeightPercent, baseH + dhPct) : undefined
        // 角点跟随：对角点固定。
        // 左/上角移动时 x/y 随之更新；宽度被 min 钳制时，x/y 回弹以保持对角点不动。
        // 未钳制时 nextW - baseW === dwPct，回弹项为 0 → nextX = currentLeft + dxPct。
        const nextX = Math.max(0, Math.min(100, currentLeft + dxPct - (nextW - baseW - dwPct)))
        const nextY = nextH != null
          ? Math.max(0, Math.min(100, currentTop + dyPct - (nextH - baseH - dhPct)))
          : Math.max(0, Math.min(100, currentTop + dyPct))

        const change: OverlayChange = {
          type: overlayType,
          id: overlayId,
          x: nextX,
          y: nextY,
          ...(nextW !== baseW ? { width: nextW } : {}),
          ...(nextH != null && nextH !== baseH ? { height: nextH } : {}),
        }
        if (onChange) {
          onChange(change)
        } else {
          setLocalPos({ x: nextX, y: nextY })
          setLocalSize({ w: nextW, h: nextH ?? 0 })
        }
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [onChange, overlayId, overlayType, currentLeft, currentTop, currentWidth, currentHeight, widthPercent, heightPercent, minWidthPercent, minHeightPercent])

  // 视觉用：move 时整体平移；resize 时尺寸/位置增量（像素）覆盖 style
  const moveTransform = isDragging ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : undefined

  // resize 期间的实际显示尺寸/位置（像素 → 直接覆盖 style）
  const resizeStyle: React.CSSProperties = resizeDelta
    ? {
        width: currentWidth != null ? `calc(${currentWidth}% + ${resizeDelta.dw}px)` : undefined,
        height: currentHeight != null ? `calc(${currentHeight}% + ${resizeDelta.dh}px)` : undefined,
        transform: `translate(${resizeDelta.dx}px, ${resizeDelta.dy}px)`,
      }
    : {}

  return (
    <div
      ref={overlayRef}
      onPointerDown={onPointerDownMove}
      className={cn(
        'group/overlay absolute z-20 cursor-move select-none rounded-sm transition-shadow',
        'hover:ring-1 hover:ring-white/30',
        isDragging && 'ring-2 ring-sky-400/80 opacity-90',
        isSelected && !isDragging && 'ring-1 ring-sky-400/70',
      )}
      style={{
        left: `${currentLeft}%`,
        top: `${currentTop}%`,
        width: currentWidth != null ? `${currentWidth}%` : undefined,
        height: currentHeight != null ? `${currentHeight}%` : undefined,
        transform: moveTransform ?? resizeStyle.transform,
        ...(resizeDelta ? { width: resizeStyle.width, height: resizeStyle.height } : {}),
        touchAction: 'none',
      }}
    >
      {children}
      {/* resize handles：仅 selected 且 resizable 时显示 */}
      {resizable && isSelected ? (
        <>
          {(['nw', 'ne', 'sw', 'se'] as Corner[]).map((corner) => (
            <span
              key={corner}
              onPointerDown={makeResizeDown(corner)}
              className={cn(
                'absolute z-30 size-2.5 rounded-full border border-sky-400 bg-white shadow-sm',
                corner === 'nw' && 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
                corner === 'ne' && 'right-0 top-0 translate-x-1/2 -translate-y-1/2',
                corner === 'sw' && 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2',
                corner === 'se' && 'right-0 bottom-0 translate-x-1/2 translate-y-1/2',
              )}
              style={{ cursor: CORNER_CURSORS[corner], touchAction: 'none' }}
            />
          ))}
        </>
      ) : null}
    </div>
  )
}

/** 逐词入场动画 → CSS animation name 映射 */
const CAPTION_ANIMATION_NAME: Record<CaptionAnimation, string> = {
  pop: 'captionPop',
  fade: 'captionFade',
  slide: 'captionSlide',
  scale: 'captionScale',
  glitch: 'captionGlitch',
  wiggle: 'captionWiggle',
  stretch: 'captionStretch',
  pulse: 'captionPulse',
  bounce: 'captionBounce',
  'blur-in': 'captionBlurIn',
  'flip-up': 'captionFlipUp',
  drift: 'captionDrift',
}

function splitWordsIntoLines(words: string[], maxWordsPerLine: number) {
  const lines: string[][] = []
  const chunkSize = Math.max(1, maxWordsPerLine)
  for (let index = 0; index < words.length; index += chunkSize) {
    lines.push(words.slice(index, index + chunkSize))
  }
  return lines
}

const CAPTION_IDLE_ANIMATION_NAME: Record<CaptionIdleAnimation, string | null> = {
  none: null,
  'drift-x': 'captionIdleDriftX',
  float: 'captionIdleFloat',
  wiggle: 'captionIdleWiggle',
  pulse: 'captionIdlePulse',
}

/** 双层 span 字幕渲染（参考 reap：描边层 + 填充层 + 逐词动画） */
function CaptionWord({
  word,
  fontFamily,
  fontSize,
  color,
  fontWeight,
  strokeColor,
  strokeWidth,
  isHighlight,
  highlightColor,
  animation,
  index,
  textShadow,
  letterSpacing,
  italic,
  highlightMode,
  highlightTextColor,
  highlightBoxColor,
  highlightUnderlineColor,
  highlightUnderlineHeight,
  borderRadius,
  paddingX,
  paddingY,
  staggerMs,
  disableBox,
  opacity = 1,
  displayColor,
  wordDurationMs,
  wordEasing,
  wordDistanceEm,
  wordScaleFrom,
}: {
  word: string
  fontFamily: string
  fontSize: number
  color: string
  fontWeight: number
  strokeColor: string
  strokeWidth: number
  isHighlight: boolean
  highlightColor?: string
  animation: CaptionAnimation
  index: number
  textShadow: string
  letterSpacing: number
  italic: boolean
  highlightMode: CaptionHighlightMode
  highlightTextColor?: string
  highlightBoxColor?: string
  highlightUnderlineColor?: string
  highlightUnderlineHeight: number
  borderRadius: number
  paddingX: number
  paddingY: number
  staggerMs: number
  disableBox?: boolean
  opacity?: number
  displayColor?: string
  wordDurationMs: number
  wordEasing: string
  wordDistanceEm: number
  wordScaleFrom: number
}) {
  const fill = displayColor ?? (isHighlight && highlightMode === 'fill' && highlightColor ? highlightColor : color)
  const strokeW = strokeWidth > 0 ? `${strokeWidth * 0.3}px` : '0'
  const animName = CAPTION_ANIMATION_NAME[animation] ?? CAPTION_ANIMATION_NAME.pop
  const showHighlightBox = !disableBox && isHighlight && highlightMode === 'box'
  const showHighlightUnderline = isHighlight && highlightMode === 'underline'
  const showHighlightGlow = isHighlight && highlightMode === 'glow'
  const highlightFill = highlightTextColor ?? color
  const underlineColor = highlightUnderlineColor ?? highlightColor ?? color

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `${animName} ${wordDurationMs}ms ${wordEasing} both`,
        animationDelay: `${index * staggerMs}ms`,
        ['--caption-word-distance' as string]: `${wordDistanceEm}em`,
        ['--caption-word-scale' as string]: `${wordScaleFrom}`,
      }}
    >
      {showHighlightBox ? (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            background: highlightBoxColor ?? highlightColor ?? '#6B46FF',
            borderRadius: `${borderRadius}px`,
            boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
          }}
        />
      ) : null}
      {/* 描边层 */}
      {strokeWidth > 0 ? (
        <span
          style={{
            position: 'absolute',
            color: strokeColor,
            WebkitTextStroke: strokeW,
            whiteSpace: 'pre',
            fontFamily: resolveFontStack(fontFamily),
            fontSize: `${fontSize}px`,
            fontWeight,
            fontStyle: italic ? 'italic' : 'normal',
            letterSpacing: `${letterSpacing}em`,
            padding: `${paddingY}px ${paddingX}px`,
          }}
        >
          {word}
        </span>
      ) : null}
      {/* 填充层 */}
      <span
        style={{
          position: 'relative',
          display: 'inline-block',
          zIndex: 1,
          whiteSpace: 'pre',
          fontFamily: resolveFontStack(fontFamily),
          fontSize: `${fontSize}px`,
          fontWeight,
          fontStyle: italic ? 'italic' : 'normal',
          letterSpacing: `${letterSpacing}em`,
          textShadow: showHighlightGlow ? `0 0 18px ${highlightColor ?? '#ffffff'}` : textShadow,
          color: showHighlightBox ? highlightFill : fill,
          padding: `${paddingY}px ${paddingX}px`,
          opacity,
        }}
      >
        {word}
      </span>
      {showHighlightUnderline ? (
        <span
          style={{
            position: 'absolute',
            left: `${paddingX}px`,
            right: `${paddingX}px`,
            bottom: `${Math.max(0, paddingY - 1)}px`,
            height: `${highlightUnderlineHeight}px`,
            borderRadius: `${Math.max(2, highlightUnderlineHeight / 2)}px`,
            background: underlineColor,
            zIndex: 0,
          }}
        />
      ) : null}
    </span>
  )
}

/**
 * Audiogram 叠加（CSS 动画模拟）。
 * 注意：本组件只负责渲染波形内容，定位/尺寸由外层 TransformableOverlay 承担。
 */
function AudiogramOverlay({ preset }: { preset: string }) {
  if (preset === 'radial') {
    return (
      <div className="flex size-full items-center justify-center opacity-70">
        <div className="relative size-16">
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="absolute left-1/2 top-1/2 h-4 w-0.5 origin-bottom bg-sky-400"
              style={{
                transform: `translate(-50%, -100%) rotate(${i * 20}deg)`,
                animation: 'barBounce 0.8s ease-in-out infinite alternate',
                animationDelay: `${i * 40}ms`,
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (preset === 'wave') {
    return (
      <div className="flex size-full items-center justify-center opacity-70">
        <svg width="100" height="24" viewBox="0 0 100 24" className="text-sky-400">
          <path d="M0 12 C8 12 8 5 16 5 S24 19 32 19 S40 7 48 7 S56 17 64 17 S72 10 80 10 S88 14 100 14" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
    )
  }

  const heights =
    preset === 'bars'
      ? [10, 14, 18, 22, 26, 30, 30, 26, 22, 18, 14, 10]
      : preset === 'hills'
        ? [3, 6, 10, 15, 20, 25, 28, 30, 28, 25, 20, 15, 10, 6, 3]
        : [4, 6, 8, 12, 16, 20, 24, 28, 24, 20, 16, 12, 8, 6, 4]

  return (
    <div className="flex h-full items-end justify-center gap-1 opacity-70">
      {heights.map((h, i) => (
        <span key={i} className="block w-1 rounded-full bg-sky-400"
          style={{ height: `${h}px`, animation: 'barBounce 0.6s ease-in-out infinite alternate', animationDelay: `${i * 50}ms` }}
        />
      ))}
    </div>
  )
}

export function TemplatePreview({
  config,
  variantConfig,
  onOverlayChange,
}: {
  config: TemplatePreviewConfig
  variantConfig?: VariantPreviewConfig | null
  onOverlayChange?: (change: OverlayChange) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(true)
  const [looping, setLooping] = useState(true)
  const [videoMs, setVideoMs] = useState(0)
  // 当前选中的 overlay（组件内部状态）。点空白处取消选中。
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null)

  // intro/outro 是拼接素材，不在预览中播放 —— 预览始终播放 main clip + overlays
  const hasIntro = Boolean(config.intro?.publicUrl)
  const hasOutro = Boolean(config.outro?.publicUrl)

  // 时间轴 tick（跟踪 main 视频，字幕同步用）
  useEffect(() => {
    let frame = 0
    const tick = () => {
      const v = videoRef.current
      if (v) {
        const durationMs = Math.max(8210, v.duration * 1000 || 8210)
        setVideoMs(((v.currentTime * 1000) % durationMs + durationMs) % durationMs)
      }
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [])

  // 初始自动播放
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.play().catch(() => {})
  }, [])

  // 加载 Web 字体（Google Fonts）—— 让字体下拉选择在预览中真实生效
  useEffect(() => {
    const id = 'fastcut-caption-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Bebas+Neue&family=Space+Grotesk:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap'
    document.head.appendChild(link)
  }, [])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) { v.play().catch(() => {}); setPlaying(true) }
    else { v.pause(); setPlaying(false) }
  }

  const toggleLoop = () => {
    const v = videoRef.current
    if (!v) return
    v.loop = !v.loop
    setLooping(v.loop)
  }

  // 点击预览区空白处取消选中。
  // overlay 在 pointerdown 时已 stopPropagation，所以走到这里说明点的是非 overlay 区域
  // （视频、背景图、或舞台本身），一律清空选中。
  const onStagePointerDown = useCallback(() => {
    setSelectedOverlayId(null)
  }, [])

  const aspectClass = config.aspectRatio === '1:1' ? 'aspect-square' : config.aspectRatio === '16:9' ? 'aspect-video' : 'aspect-[9/16]'
  const logos = config.logos ?? []

  // 字幕参数
  const renderConfig = variantConfig
  const fontFamily = renderConfig?.fontFamily ?? 'Inter'
  const captionFontStack = resolveFontStack(fontFamily)
  const fontSize = Math.max(14, (renderConfig?.fontSize ?? 42) * 0.5)
  const color = renderConfig?.color ?? '#FFFFFF'
  const fontWeight = renderConfig?.fontWeight ?? 700
  const strokeColor = renderConfig?.strokeColor ?? '#000000'
  const strokeWidth = renderConfig?.strokeWidth ?? 0
  const highlightColor = renderConfig?.highlightColor
  const textTransform = renderConfig?.textTransform ?? 'uppercase'
  const letterSpacing = renderConfig?.letterSpacing ?? -0.03
  const lineHeight = renderConfig?.lineHeight ?? 1.08
  const captionTextAlign = renderConfig?.textAlign ?? 'center'
  const textShadow = renderConfig?.textShadow ?? '0 2px 10px rgba(0,0,0,0.45)'
  const wordGap = renderConfig?.wordGap ?? 10
  const maxWordsPerLine = renderConfig?.maxWordsPerLine ?? 3
  const staggerMs = renderConfig?.staggerMs ?? 90
  const italic = renderConfig?.italic ?? false
  const highlightMode = renderConfig?.highlightMode ?? 'fill'
  const boxScope = renderConfig?.boxScope ?? 'word'
  const highlightLineIndex = renderConfig?.highlightLineIndex ?? 0
  const highlightTextColor = renderConfig?.highlightTextColor
  const highlightBoxColor = renderConfig?.highlightBoxColor
  const highlightUnderlineColor = renderConfig?.highlightUnderlineColor
  const highlightUnderlineHeight = renderConfig?.highlightUnderlineHeight ?? 6
  const borderRadius = renderConfig?.borderRadius ?? 12
  const paddingX = renderConfig?.paddingX ?? 10
  const paddingY = renderConfig?.paddingY ?? 6
  const idleAnimation = renderConfig?.idleAnimation ?? 'none'
  const wordDurationMs = renderConfig?.wordDurationMs ?? 480
  const wordEasing = renderConfig?.wordEasing ?? 'cubic-bezier(0.22, 1, 0.36, 1)'
  const wordDistanceEm = renderConfig?.wordDistanceEm ?? 0.62
  const wordScaleFrom = renderConfig?.wordScaleFrom ?? 0.42
  const idleDurationMs = renderConfig?.idleDurationMs ?? 2200
  const idleOffsetPx = renderConfig?.idleOffsetPx ?? 8
  const sleeperColor = renderConfig?.sleeperColor
  const sleeperOpacity = renderConfig?.sleeperOpacity ?? 0.78
  const backgroundColor = renderConfig?.backgroundColor
  const backgroundTextColor = renderConfig?.backgroundTextColor
  const backgroundRadius = renderConfig?.backgroundRadius ?? 12
  const backgroundPaddingX = renderConfig?.backgroundPaddingX ?? 16
  const backgroundPaddingY = renderConfig?.backgroundPaddingY ?? 6
  const backgroundShadow = renderConfig?.backgroundShadow ?? '0 6px 16px rgba(0,0,0,0.18)'
  const lineOffsetY = renderConfig?.lineOffsetY ?? 0

  const cues = buildPreviewCaptionCues(maxWordsPerLine)
  const fallbackCue = cues[0] ?? { text: '', startMs: 0, endMs: 0, words: [] }
  const activeCue = cues.find((cue) => videoMs >= cue.startMs && videoMs < cue.endMs) ?? fallbackCue
  const displayWords = activeCue.words.map((item) => (
    textTransform === 'uppercase'
      ? item.word.toUpperCase()
      : item.word.toLowerCase() === 'of'
        ? 'of'
        : item.word.charAt(0).toUpperCase() + item.word.slice(1).toLowerCase()
  ))
  const captionLines = splitWordsIntoLines(displayWords, maxWordsPerLine)
  const activeWordIndex = activeCue.words.findIndex((word) => videoMs >= word.startMs && videoMs < word.endMs)
  const highlightIndex = activeWordIndex >= 0 ? activeWordIndex : 0

  // 逐词入场动画类型：由选中的字幕样式 key 决定（来自 captionStyleConfigMap）
  const captionAnimation: CaptionAnimation = renderConfig?.wordAnimation ?? 'pop'
  const idleAnimationName = CAPTION_IDLE_ANIMATION_NAME[idleAnimation] ?? null
  const showLineBox = boxScope === 'line' && (highlightMode === 'box' || Boolean(backgroundColor))

  // 字幕位置/宽度：优先用配置中的百分比坐标，缺省 10% / 70% / 80%
  // 字幕是文本流，高度由行数 + 字号自动撑开，只控制宽度
  const captionX = typeof config.captions?.x === 'number' ? config.captions.x : 10
  const captionY = typeof config.captions?.y === 'number' ? config.captions.y : 70
  const captionWidth = typeof config.captions?.width === 'number' ? config.captions.width : 80

  // Audiogram 位置/尺寸：优先用配置中的百分比坐标。
  // 默认顶部居中靠下（水平居中 = (100-width)/2，y 18% 在顶部下方一点），
  // 避免与底部字幕 / 角落 logo 重叠。
  const audiogramEnabled = config.audiogram?.enabled !== false
  const audiogramWidth = typeof config.audiogram?.width === 'number' ? config.audiogram.width : 40
  const audiogramHeight = typeof config.audiogram?.height === 'number' ? config.audiogram.height : 10
  const audiogramX = typeof config.audiogram?.x === 'number' ? config.audiogram.x : (100 - audiogramWidth) / 2
  const audiogramY = typeof config.audiogram?.y === 'number' ? config.audiogram.y : 18
  const textEnabled = config.text?.enabled !== false
  const textWidth = typeof config.text?.width === 'number' ? config.text.width : 60
  const textHeight = typeof config.text?.height === 'number' ? config.text.height : 14
  const textX = typeof config.text?.x === 'number' ? config.text.x : 20
  const textY = typeof config.text?.y === 'number' ? config.text.y : 40
  const textPresetPreview = getTextPresetPreview(config.textPreset, config.text)
  const textAlign = config.text?.align ?? 'center'
  const textAnimationName = getTextAnimationName(config.text?.animation)

  return (
    <div className="flex flex-col">
      <div
        className={`relative ${aspectClass} w-full overflow-hidden rounded-sm bg-black`}
        onPointerDown={onStagePointerDown}
      >
        {/* 主视频（talking-head）—— 始终播放，overlays 叠加在上面 */}
        <video
          ref={videoRef}
          src={PREVIEW_VIDEO}
          className="size-full object-cover"
          muted
          loop
          playsInline
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />

        {/* 自定义背景图 overlay */}
        {config.backgroundEnabled !== false && config.background?.publicUrl ? (
          <img src={resolveUrl(config.background.publicUrl)} alt="" className="absolute inset-0 size-full object-cover opacity-30" />
        ) : null}

        {/* Logo overlay（可拖拽 + 可 resize） */}
        {config.logoEnabled !== false ? (
          logos.map((logo, i) => {
          const fallback = logoInitialPosition(logo.position)
          const lx = typeof logo.x === 'number' ? logo.x : fallback.left
          const ly = typeof logo.y === 'number' ? logo.y : fallback.top
          const lw = typeof logo.width === 'number' ? logo.width : 12
          const logoId = `logo-${i}`
          return (
            <TransformableOverlay
              key={logoId}
              overlayId={logoId}
              overlayType="logo"
              selectedId={selectedOverlayId}
              onSelect={setSelectedOverlayId}
              leftPercent={lx}
              topPercent={ly}
              widthPercent={lw}
              onChange={(change) => onOverlayChange?.({ ...change, id: i })}
            >
              {logo.publicUrl ? (
                <img
                  src={resolveUrl(logo.publicUrl)}
                  alt=""
                  className="size-full object-contain"
                  draggable={false}
                />
              ) : null}
            </TransformableOverlay>
          )
          })
        ) : null}

        {/* Audiogram 叠加（可拖拽 + 可 resize） */}
        {config.audiogramPreset && audiogramEnabled ? (
          <TransformableOverlay
            overlayId="audiogram"
            overlayType="audiogram"
            selectedId={selectedOverlayId}
            onSelect={setSelectedOverlayId}
            leftPercent={audiogramX}
            topPercent={audiogramY}
            widthPercent={audiogramWidth}
            heightPercent={audiogramHeight}
            onChange={(change) => onOverlayChange?.(change)}
          >
            <AudiogramOverlay preset={config.audiogramPreset} />
          </TransformableOverlay>
        ) : null}

        {/* 字幕叠加（可拖拽 + 可 resize 宽度）。
            右侧主预览继续用 talking-head 视频，字幕层由 style config 驱动 DOM 渲染：
            双层 span 描边 + 高亮 + 逐词 CSS 动画（pop/fade/slide 等）。
            样式卡片 grid 仍用 /templates/<key>.mp4 做 preview asset，两者职责分离。
            仅在 main 段落显示字幕 / logo / audiogram / text overlay，intro/outro 是完整视频不打扰。 */}
        {config.textPreset && textEnabled ? (
          <TransformableOverlay
            overlayId="text"
            overlayType="text"
            selectedId={selectedOverlayId}
            onSelect={setSelectedOverlayId}
            leftPercent={textX}
            topPercent={textY}
            widthPercent={textWidth}
            heightPercent={textHeight}
            onChange={(change) => onOverlayChange?.(change)}
          >
            <div
              className="flex size-full items-center"
              style={{
                justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
                  justifyContent: 'center',
                  textAlign,
                  animation: textAnimationName ? `${textAnimationName} 720ms cubic-bezier(.2,.8,.2,1) both` : undefined,
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
                    justifyContent: 'center',
                    textAlign,
                    textShadow: '0 2px 10px rgba(0,0,0,0.28)',
                    ...textPresetPreview.style,
                  }}
                >
                  {textPresetPreview.lines.map((line, index) => (
                    <span key={`${index}-${line}`}>{line}</span>
                  ))}
                </div>
              </div>
            </div>
          </TransformableOverlay>
        ) : null}

        {config.captions?.enabled !== false && variantConfig ? (
          <TransformableOverlay
            overlayId="caption"
            overlayType="caption"
            selectedId={selectedOverlayId}
            onSelect={setSelectedOverlayId}
            leftPercent={captionX}
            topPercent={captionY}
            widthPercent={captionWidth}
            onChange={(change) => onOverlayChange?.(change)}
          >
            <div
              className="w-full"
              style={{
                fontFamily: captionFontStack,
                fontSize: `${fontSize}px`,
                fontWeight,
                lineHeight,
                textAlign: captionTextAlign,
                fontStyle: italic ? 'italic' : 'normal',
              }}
            >
              {captionLines.map((lineWords, lineIndex) => (
                <div
                  key={`line-${lineIndex}`}
                  style={{
                    display: 'flex',
                    justifyContent: captionTextAlign === 'left' ? 'flex-start' : captionTextAlign === 'right' ? 'flex-end' : 'center',
                    gap: `${wordGap}px`,
                    marginTop: lineIndex > 0 ? `${Math.max(2, paddingY * 0.5) + lineOffsetY}px` : undefined,
                  }}
                >
                  {showLineBox && lineIndex === highlightLineIndex ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        gap: `${wordGap}px`,
                        padding: `${backgroundPaddingY}px ${backgroundPaddingX}px`,
                        borderRadius: `${backgroundRadius}px`,
                        background: backgroundColor ?? highlightBoxColor ?? highlightColor ?? '#6B46FF',
                        boxShadow: backgroundShadow,
                        animation: idleAnimationName ? `${idleAnimationName} ${idleDurationMs}ms ease-in-out infinite alternate` : undefined,
                        ['--caption-idle-offset' as string]: `${idleOffsetPx}px`,
                      }}
                    >
                      {lineWords.map((word, wordIndex) => {
                        const flatIndex = captionLines.slice(0, lineIndex).reduce((total, line) => total + line.length, 0) + wordIndex
                        return (
                          <CaptionWord
                            key={`${lineIndex}-${wordIndex}`}
                            word={word}
                            fontFamily={fontFamily}
                            fontSize={fontSize}
                            color={color}
                            fontWeight={fontWeight}
                            strokeColor={strokeColor}
                            strokeWidth={strokeWidth}
                            isHighlight
                            highlightColor={highlightColor}
                            animation={captionAnimation}
                            index={flatIndex}
                            textShadow={textShadow}
                            letterSpacing={letterSpacing}
                            italic={italic}
                            highlightMode={highlightMode}
                            highlightTextColor={backgroundTextColor ?? highlightTextColor}
                            highlightBoxColor={highlightBoxColor}
                            highlightUnderlineColor={highlightUnderlineColor}
                            highlightUnderlineHeight={highlightUnderlineHeight}
                            borderRadius={backgroundRadius}
                            paddingX={0}
                            paddingY={0}
                            staggerMs={staggerMs}
                            disableBox
                            wordDurationMs={wordDurationMs}
                            wordEasing={wordEasing}
                            wordDistanceEm={wordDistanceEm}
                            wordScaleFrom={wordScaleFrom}
                          />
                        )
                      })}
                    </div>
                  ) : (
                    lineWords.map((word, wordIndex) => {
                      const flatIndex = captionLines.slice(0, lineIndex).reduce((total, line) => total + line.length, 0) + wordIndex
                      return (
                        <div
                          key={`${lineIndex}-${wordIndex}`}
                          style={{
                            animation: idleAnimationName ? `${idleAnimationName} ${idleDurationMs}ms ease-in-out infinite alternate` : undefined,
                            animationDelay: idleAnimationName ? `${flatIndex * 70}ms` : undefined,
                            ['--caption-idle-offset' as string]: `${idleOffsetPx}px`,
                          }}
                        >
                          <CaptionWord
                            word={word}
                            fontFamily={fontFamily}
                            fontSize={fontSize}
                            color={color}
                            fontWeight={fontWeight}
                            strokeColor={strokeColor}
                            strokeWidth={strokeWidth}
                            isHighlight={flatIndex === highlightIndex}
                            highlightColor={highlightColor}
                            animation={captionAnimation}
                            index={flatIndex}
                            textShadow={textShadow}
                            letterSpacing={letterSpacing}
                            italic={italic}
                            highlightMode={highlightMode}
                            highlightTextColor={highlightTextColor}
                            highlightBoxColor={highlightBoxColor}
                            highlightUnderlineColor={highlightUnderlineColor}
                            highlightUnderlineHeight={highlightUnderlineHeight}
                            borderRadius={borderRadius}
                            paddingX={paddingX}
                            paddingY={paddingY}
                            staggerMs={staggerMs}
                            opacity={flatIndex === highlightIndex || !sleeperColor ? 1 : sleeperOpacity}
                            displayColor={flatIndex === highlightIndex ? undefined : sleeperColor}
                            wordDurationMs={wordDurationMs}
                            wordEasing={wordEasing}
                            wordDistanceEm={wordDistanceEm}
                            wordScaleFrom={wordScaleFrom}
                          />
                        </div>
                      )
                    })
                  )}
                </div>
              ))}
            </div>
          </TransformableOverlay>
        ) : null}

        {/* 拼接时间线标注：标示 intro / main / outro 的拼接结构（不在预览中播放 intro/outro） */}
        {(hasIntro || hasOutro) ? (
          <div className="absolute inset-x-0 bottom-0 z-30 flex items-stretch gap-px px-0 text-[9px] font-bold tracking-wide text-white/70">
            {hasIntro ? (
              <div className="flex items-center justify-center bg-indigo-600/70 px-2 py-0.5 backdrop-blur-sm">片头</div>
            ) : null}
            <div className="flex flex-1 items-center justify-center bg-sky-600/70 px-2 py-0.5 backdrop-blur-sm">主体</div>
            {hasOutro ? (
              <div className="flex items-center justify-center bg-indigo-600/70 px-2 py-0.5 backdrop-blur-sm">片尾</div>
            ) : null}
          </div>
        ) : null}

        <style>{`
          @keyframes barBounce { 0% { transform: scaleY(0.3); } 100% { transform: scaleY(1.3); } }
          @keyframes captionPop { 0% { opacity: 0; transform: scale(var(--caption-word-scale, 0.42)); } 70% { transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes captionFade { 0% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes captionSlide { 0% { opacity: 0; transform: translateY(var(--caption-word-distance, 0.6em)); } 100% { opacity: 1; transform: translateY(0); } }
          @keyframes captionScale { 0% { opacity: 0; transform: scale(var(--caption-word-scale, 0.2)); } 60% { transform: scale(1.1); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes captionGlitch { 0% { opacity: 0; transform: translate(-3px, 2px); } 25% { transform: translate(3px, -2px); } 50% { transform: translate(-2px, -2px); } 75% { transform: translate(2px, 2px); } 100% { opacity: 1; transform: translate(0, 0); } }
          @keyframes captionWiggle { 0% { opacity: 0; transform: rotate(-6deg) scale(var(--caption-word-scale, 0.8)); } 60% { transform: rotate(4deg) scale(1.05); } 100% { opacity: 1; transform: rotate(0) scale(1); } }
          @keyframes captionStretch { 0% { opacity: 0; transform: scaleX(0.3); } 100% { opacity: 1; transform: scaleX(1); } }
          @keyframes captionPulse { 0% { opacity: 0; transform: scale(calc(var(--caption-word-scale, 0.8) + 0.22)); } 50% { transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes captionBounce { 0% { opacity: 0; transform: translateY(calc(var(--caption-word-distance, 0.7em) * 1.15)) scale(calc(var(--caption-word-scale, 0.7) + 0.12)); } 55% { opacity: 1; transform: translateY(-0.08em) scale(1.06); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
          @keyframes captionBlurIn { 0% { opacity: 0; filter: blur(10px); transform: scale(calc(var(--caption-word-scale, 1) + 0.63)); } 100% { opacity: 1; filter: blur(0); transform: scale(1); } }
          @keyframes captionFlipUp { 0% { opacity: 0; transform: perspective(300px) rotateX(-80deg) translateY(var(--caption-word-distance, 0.6em)); } 100% { opacity: 1; transform: perspective(300px) rotateX(0) translateY(0); } }
          @keyframes captionDrift { 0% { opacity: 0; transform: translateX(calc(var(--caption-word-distance, 0.4em) * -0.8)) translateY(calc(var(--caption-word-distance, 0.4em) * 0.35)); } 100% { opacity: 1; transform: translateX(0) translateY(0); } }
          @keyframes captionIdleDriftX { 0% { transform: translateX(calc(var(--caption-idle-offset, 8px) * -1)); } 100% { transform: translateX(var(--caption-idle-offset, 8px)); } }
          @keyframes captionIdleFloat { 0% { transform: translateY(calc(var(--caption-idle-offset, 6px) * 0.5)); } 100% { transform: translateY(calc(var(--caption-idle-offset, 6px) * -0.5)); } }
          @keyframes captionIdleWiggle { 0% { transform: rotate(-2.4deg); } 100% { transform: rotate(2.4deg); } }
          @keyframes captionIdlePulse { 0% { transform: scale(0.98); opacity: 0.94; } 100% { transform: scale(1.02); opacity: 1; } }
          @keyframes textFadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes textScaleIn { 0% { opacity: 0; transform: scale(0.7); } 65% { opacity: 1; transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes textBounceIn { 0% { opacity: 0; transform: scale(0.45) translateY(14px); } 55% { opacity: 1; transform: scale(1.08) translateY(-4px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes textFlipIn { 0% { opacity: 0; transform: perspective(340px) rotateX(-72deg); } 100% { opacity: 1; transform: perspective(340px) rotateX(0); } }
          @keyframes textZoomIn { 0% { opacity: 0; transform: scale(1.45); filter: blur(10px); } 100% { opacity: 1; transform: scale(1); filter: blur(0); } }
          @keyframes textSlideUp { 0% { opacity: 0; transform: translateY(18px); } 100% { opacity: 1; transform: translateY(0); } }
          @keyframes textSnapIn { 0% { opacity: 0; transform: scale(0.92) rotate(-6deg); } 65% { opacity: 1; transform: scale(1.02) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
          @keyframes textGlitchIn { 0% { opacity: 0; transform: translate(-6px, 3px); } 25% { transform: translate(4px, -2px); } 50% { transform: translate(-2px, -1px); } 100% { opacity: 1; transform: translate(0, 0); } }
          @keyframes textSwipeIn { 0% { opacity: 0; clip-path: inset(0 100% 0 0); } 100% { opacity: 1; clip-path: inset(0 0 0 0); } }
          @keyframes textFloatIn { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
          @keyframes textSpinIn { 0% { opacity: 0; transform: rotate(-18deg) scale(0.86); } 100% { opacity: 1; transform: rotate(0) scale(1); } }
          @keyframes textSlideDownIn { 0% { opacity: 0; transform: translateY(-18px); } 100% { opacity: 1; transform: translateY(0); } }
          @keyframes textSlideLeftIn { 0% { opacity: 0; transform: translateX(-18px); } 100% { opacity: 1; transform: translateX(0); } }
          @keyframes textDiagonalIn { 0% { opacity: 0; transform: translate(-16px, 16px); } 100% { opacity: 1; transform: translate(0, 0); } }
          @keyframes textWobbleIn { 0% { opacity: 0; transform: translateX(-14px) rotate(-8deg); } 45% { opacity: 1; transform: translateX(8px) rotate(5deg); } 70% { transform: translateX(-4px) rotate(-3deg); } 100% { opacity: 1; transform: translateX(0) rotate(0); } }
          @keyframes textFlipYIn { 0% { opacity: 0; transform: perspective(340px) rotateY(-84deg); } 100% { opacity: 1; transform: perspective(340px) rotateY(0); } }
          @keyframes textPulseIn { 0% { opacity: 0; transform: scale(1.2); } 50% { opacity: 1; transform: scale(0.94); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes textDropIn { 0% { opacity: 0; transform: translateY(-32px) scale(0.94); } 80% { opacity: 1; transform: translateY(4px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
          @keyframes textSqueezeIn { 0% { opacity: 0; transform: scaleX(1.45) scaleY(0.35); } 100% { opacity: 1; transform: scaleX(1) scaleY(1); } }
          @keyframes textRollIn { 0% { opacity: 0; transform: translateX(-26px) rotate(-18deg); } 100% { opacity: 1; transform: translateX(0) rotate(0); } }
          @keyframes textSwingIn { 0% { opacity: 0; transform: rotate(-14deg); transform-origin: top center; } 55% { opacity: 1; transform: rotate(8deg); } 100% { opacity: 1; transform: rotate(0); transform-origin: top center; } }
          @keyframes textExpandIn { 0% { opacity: 0; transform: scale(0.35); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes textExpandHIn { 0% { opacity: 0; transform: scaleX(0.18); } 100% { opacity: 1; transform: scaleX(1); } }
          @keyframes textTwistIn { 0% { opacity: 0; transform: rotate(-22deg) skewX(-12deg); } 100% { opacity: 1; transform: rotate(0) skewX(0); } }
          @keyframes textBlurIn { 0% { opacity: 0; filter: blur(14px); } 100% { opacity: 1; filter: blur(0); } }
          @keyframes textSpiralIn { 0% { opacity: 0; transform: rotate(-120deg) scale(0.2); } 100% { opacity: 1; transform: rotate(0) scale(1); } }
          @keyframes textShakeIn { 0% { opacity: 0; transform: translateX(-12px); } 20% { transform: translateX(12px); } 40% { transform: translateX(-8px); } 60% { transform: translateX(8px); } 80% { transform: translateX(-3px); } 100% { opacity: 1; transform: translateX(0); } }
          @keyframes textCurtainIn { 0% { opacity: 0; clip-path: inset(0 50% 0 50%); } 100% { opacity: 1; clip-path: inset(0 0 0 0); } }
          @keyframes textFoldIn { 0% { opacity: 0; transform: perspective(340px) rotateX(-90deg) scaleY(0.3); transform-origin: top center; } 100% { opacity: 1; transform: perspective(340px) rotateX(0) scaleY(1); transform-origin: top center; } }
          @keyframes textZigzagIn { 0% { opacity: 0; transform: translate(-18px, 10px); } 25% { transform: translate(12px, -8px); } 50% { transform: translate(-8px, 6px); } 75% { transform: translate(4px, -3px); } 100% { opacity: 1; transform: translate(0, 0); } }
          @keyframes textElasticIn { 0% { opacity: 0; transform: scale(0.4); } 60% { opacity: 1; transform: scale(1.12); } 78% { transform: scale(0.96); } 100% { opacity: 1; transform: scale(1); } }
          @keyframes textSlingshotIn { 0% { opacity: 0; transform: translateX(-28px) scale(0.8); } 70% { opacity: 1; transform: translateX(6px) scale(1.06); } 100% { opacity: 1; transform: translateX(0) scale(1); } }
          @keyframes textRotateIn { 0% { opacity: 0; transform: rotate(-32deg); } 100% { opacity: 1; transform: rotate(0); } }
          @keyframes textSkewIn { 0% { opacity: 0; transform: skewX(-22deg); } 100% { opacity: 1; transform: skewX(0); } }
          @keyframes textPeekIn { 0% { opacity: 0; clip-path: inset(100% 0 0 0); transform: translateY(8px); } 100% { opacity: 1; clip-path: inset(0 0 0 0); transform: translateY(0); } }
          @keyframes textVortexIn { 0% { opacity: 0; transform: rotate(-180deg) scale(0.1); filter: blur(12px); } 100% { opacity: 1; transform: rotate(0) scale(1); filter: blur(0); } }
        `}</style>
      </div>

      {/* 播放控件 */}
      <div className="flex items-center justify-between border-x border-b border-hairline bg-black px-4 py-2 text-white">
        <button type="button" onClick={togglePlay} className="text-white/80 transition-colors hover:text-white">
          {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
        </button>
        <button type="button" onClick={toggleLoop} className={`transition-colors ${looping ? 'text-sky-400' : 'text-white/40 hover:text-white/80'}`}>
          <Repeat className="size-4" />
        </button>
      </div>
    </div>
  )
}
